import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import GeminiWarnung, { geminiSperreNeuLaden } from "../components/GeminiWarnung";
import { cacheLesen, cacheSchreiben } from "../utils/seitenCache";

const API = `${API_BASE}/postkorb/api`;
const CACHE_KEY = "postkorb.uebersicht";

const AMPEL = {
  gruen: { farbe: "var(--color-success)", zeichen: "✓" },
  gelb:  { farbe: "var(--color-warning)", zeichen: "!" },
  rot:   { farbe: "var(--color-error)",   zeichen: "!" },
  blau:  { farbe: "var(--color-info)",    zeichen: "…" },
  grau:  { farbe: "var(--color-gray-400)", zeichen: "–" },
};

const S = {
  page: { maxWidth: 900, margin: "0 auto", padding: "24px 20px 60px", color: "var(--color-gray-800)" },
  h1: { fontSize: "1.5rem", margin: "0 0 4px" },
  sub: { color: "var(--color-gray-600)", margin: "0 0 8px", fontSize: "0.9rem" },

  kopfBox: { display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderRadius: 10, marginBottom: 22 },
  kopfZahl: { fontSize: "1.6rem", fontWeight: 700, lineHeight: 1 },
  kopfText: { fontSize: "0.95rem", fontWeight: 600 },

  block: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, marginBottom: 16, overflow: "hidden" },
  blockKopf: { display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: "1px solid var(--color-gray-200)" },
  ampelPunkt: (farbe) => ({
    width: 26, height: 26, borderRadius: "50%", background: farbe, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: "0.9rem", flexShrink: 0,
  }),
  blockTitel: { fontSize: "1.05rem", fontWeight: 600, margin: 0 },
  blockLauf: { fontSize: "0.8rem", color: "var(--color-gray-600)", marginLeft: "auto", textAlign: "right" },

  inhalt: { padding: "6px 18px 16px" },
  okZeile: { display: "flex", alignItems: "center", gap: 8, padding: "10px 0", color: "var(--color-success)", fontSize: "0.92rem", fontWeight: 600 },

  abschnitt: { marginTop: 12 },
  abschnittTitel: { fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-gray-600)", marginBottom: 8 },

  punkt: { borderLeft: "3px solid var(--color-error)", background: "var(--color-error-bg, rgba(220,38,38,0.06))", borderRadius: "0 6px 6px 0", padding: "10px 14px", marginBottom: 8 },
  punktHinweis: { borderLeft: "3px solid var(--color-warning)", background: "var(--color-warning-bg, rgba(217,119,6,0.06))", borderRadius: "0 6px 6px 0", padding: "10px 14px", marginBottom: 8 },
  punktTitel: { fontWeight: 600, fontSize: "0.88rem", wordBreak: "break-word", marginBottom: 3 },
  punktProblem: { fontSize: "0.85rem", marginBottom: 4 },
  punktTun: { fontSize: "0.85rem", color: "var(--color-gray-700)" },
  punktZusatz: { fontSize: "0.78rem", color: "var(--color-gray-600)", marginTop: 4 },

  historie: { background: "var(--bg-card)", border: "1px solid var(--color-gray-200)", borderRadius: 10, padding: 18, marginTop: 26 },
  histTitel: { fontSize: "1rem", fontWeight: 600, margin: "0 0 12px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" },
  th: { textAlign: "left", padding: "6px 10px", borderBottom: "2px solid var(--color-gray-300)", color: "var(--color-gray-700)", whiteSpace: "nowrap" },
  td: { padding: "6px 10px", borderBottom: "1px solid var(--color-gray-200)" },
  btn: { padding: "7px 14px", border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--color-gray-100)", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 },
  btnKlein: { padding: "5px 12px", border: "1px solid var(--color-gray-300)", borderRadius: 5, background: "var(--color-gray-100)", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600 },
  aufklapp: { padding: "7px 12px", border: "1px solid var(--color-gray-300)", borderRadius: 6, background: "var(--color-gray-100)", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, width: "100%", textAlign: "left" },
  erledigtZeile: { display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", borderBottom: "1px solid var(--color-gray-200)", fontSize: "0.82rem" },
  leer: { color: "var(--color-gray-600)", fontSize: "0.88rem", padding: "8px 0" },
};

function zeit(wert) {
  if (!wert) return "—";
  try {
    return new Date(wert).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(wert); }
}

// Welle 63: Zeit-/An-Aus-Einstellung der Bilder-Komprimier-Automatik direkt in der Postkorb-Kachel.
function BilderKomprConfig() {
  const URL = `${API_BASE}/zuordnen/api/bilder-kompr-auto/config`;
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    fetch(URL).then((r) => r.json()).then((d) => d.success && setCfg(d.config)).catch(() => {});
  }, [URL]);
  const speichern = async (patch) => {
    setSaving(true); setMsg("");
    try {
      const r = await fetch(URL, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) });
      const d = await r.json();
      if (d.success) { setCfg(d.config); setMsg("gespeichert"); setTimeout(() => setMsg(""), 1500); }
    } finally { setSaving(false); }
  };
  if (!cfg) return null;
  return (
    <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--color-gray-50, #f8fafc)", border: "1px solid var(--color-gray-200)", borderRadius: 8, fontSize: "0.82rem" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input type="checkbox" checked={cfg.enabled} disabled={saving}
          onChange={(e) => speichern({ enabled: e.target.checked })} />
        <span>Automatik <b>{cfg.enabled ? "AN" : "AUS"}</b> — alle Bilder täglich komprimieren (+ Original auf Synology)</span>
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <span>Uhrzeit:</span>
        <input type="time" defaultValue={cfg.uhrzeit} disabled={saving}
          onBlur={(e) => e.target.value && e.target.value !== cfg.uhrzeit && speichern({ uhrzeit: e.target.value })}
          style={{ padding: "2px 6px" }} />
        {msg && <span style={{ color: "var(--color-success, #16a34a)" }}>✓ {msg}</span>}
      </div>
    </div>
  );
}

// Rename-me Auto-Batch: Intervall + Pipeline-Wahl (Standard/Tesseract ↔ Gemini)
// + nächster Lauf. Nutzt den bestehenden Auto-Batch-Config-Endpoint.
function RenamemeConfig({ autoInfo }) {
  const URL = `${API_BASE}/rename-me-ocr/api/auto-batch/config`;
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    fetch(URL).then((r) => r.json()).then((d) => d && !d.detail && setCfg(d)).catch(() => {});
  }, [URL]);
  const speichern = async (patch) => {
    setSaving(true); setMsg("");
    try {
      const r = await fetch(URL, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch) });
      const d = await r.json();
      if (d && !d.detail) { setCfg(d); setMsg("gespeichert"); setTimeout(() => setMsg(""), 1500); }
    } finally { setSaving(false); }
  };
  if (!cfg) return null;
  const intervalle = [5, 10, 15, 30, 60, 120];
  const sp = cfg.schema_pipelines || {};
  const setSchema = (schema, val) => {
    const next = { ...sp };
    if (!val) delete next[schema]; else next[schema] = val;
    speichern({ schema_pipelines: next });
  };
  return (
    <div style={{ marginTop: 8, padding: "8px 10px", background: "var(--color-gray-50, #f8fafc)", border: "1px solid var(--color-gray-200)", borderRadius: 8, fontSize: "0.82rem" }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input type="checkbox" checked={!!cfg.enabled} disabled={saving}
          onChange={(e) => speichern({ enabled: e.target.checked })} />
        <span>Auto-Batch <b>{cfg.enabled ? "AN" : "AUS"}</b>
          {cfg.enabled && autoInfo?.naechster_lauf ? ` — nächster Lauf: ${zeit(autoInfo.naechster_lauf)}` : ""}</span>
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <span>Alle</span>
        <select value={cfg.interval_minutes || 15} disabled={saving}
          onChange={(e) => speichern({ interval_minutes: Number(e.target.value) })} style={{ padding: "2px 6px" }}>
          {intervalle.map((m) => <option key={m} value={m}>{m} Min</option>)}
        </select>
        <span style={{ marginLeft: 10 }}>Standard-Engine:</span>
        <select value={cfg.pipeline_version || "v1"} disabled={saving}
          onChange={(e) => speichern({ pipeline_version: e.target.value })} style={{ padding: "2px 6px" }}>
          <option value="v1">Standard (MuPDF + Tesseract)</option>
          <option value="gemini">✨ Gemini</option>
          <option value="v2">Legacy (Claude / Qwen)</option>
        </select>
        {msg && <span style={{ color: "var(--color-success, #16a34a)" }}>✓ {msg}</span>}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer" }}
        title="Welle 79: Jedes Dokument wird von v1 UND Gemini benannt; die Datei heißt wie die Schema-Matrix vorgibt, der andere Vorschlag steht in der Karte als Alternative">
        <input type="checkbox" checked={!!cfg.beide_pipelines} disabled={saving}
          onChange={(e) => speichern({ beide_pipelines: e.target.checked })} />
        <span>✨ Beide Pipelines rechnen (v1 + Gemini) — Alternative in der Karte wählbar</span>
      </label>
      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: "pointer", fontSize: "0.78rem", color: "var(--color-gray-600)" }}>
          Pro Dokumenttyp einstellen (Schema wird immer zuerst per Standard erkannt{cfg.beide_pipelines ? "; bei ‚beide‘ = welcher Name vorausgewählt ist" : ""})
        </summary>
        <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 10px", alignItems: "center" }}>
          {SCHEMA_LISTE.map((s) => (
            <React.Fragment key={s.key}>
              <span style={{ color: "var(--color-gray-700)" }}>{s.label}</span>
              {s.key === "ausgangsrechnung" ? (
                <span style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
                  immer Standard (v1) — Gemini erstellt kein Duplikat
                </span>
              ) : (
                <select value={sp[s.key] || ""} disabled={saving}
                  onChange={(e) => setSchema(s.key, e.target.value)} style={{ padding: "2px 6px", maxWidth: 220 }}>
                  <option value="">Standard-Engine (global)</option>
                  <option value="v1">Immer Standard (v1)</option>
                  <option value="gemini">✨ Immer Gemini</option>
                  <option value="v1_then_gemini">v1, sonst Gemini</option>
                </select>
              )}
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: "0.72rem", color: "var(--color-gray-500)" }}>
          Empfehlung: Kontoauszüge & Turnierabrechnungen → Standard; Rechnungen → Gemini;
          Sonstiges → „v1, sonst Gemini".
        </div>
      </details>
    </div>
  );
}

// Bekannte Schema-Codes (Referenz DOKUMENTTYP_SCHEMA_MAP im Backend)
const SCHEMA_LISTE = [
  { key: "rechnung", label: "Rechnung (Eingang)" },
  { key: "ausgangsrechnung", label: "Ausgangsrechnung" },
  { key: "mahnung", label: "Mahnung" },
  { key: "kontoauszug", label: "Kontoauszug" },
  { key: "versicherung", label: "Versicherung" },
  { key: "versorger", label: "Versorger (Strom/Gas/…)" },
  { key: "vertrag", label: "Vertrag" },
  { key: "bwa", label: "BWA / UStVA" },
  { key: "finanzamt", label: "Finanzamt" },
  { key: "auslage", label: "Auslage / Beleg" },
  { key: "turnierabrechnung", label: "Turnierabrechnung" },
  { key: "gehaltsabrechnung", label: "Gehaltsabrechnung" },
  { key: "sonstiges", label: "Sonstiges" },
];

function Block({ q, onAktion }) {
  const [zeigeErledigt, setZeigeErledigt] = useState(false);
  const [zeigeKonten, setZeigeKonten] = useState(false);
  const [laeuftAktion, setLaeuftAktion] = useState("");
  const a = AMPEL[q.ampel] || AMPEL.grau;
  const handlung = q.handlung || [];
  const hinweise = q.hinweise || [];
  const erledigt = q.erledigt || [];
  const sauber = handlung.length === 0 && hinweise.length === 0;

  async function aktion(typ, punkt) {
    setLaeuftAktion(punkt.schluessel || punkt.titel);
    try { await onAktion(typ, q.key, punkt); } finally { setLaeuftAktion(""); }
  }

  return (
    <div style={{ ...S.block, borderColor: q.ampel === "rot" ? "var(--color-error)" : "var(--color-gray-200)" }}>
      <div style={S.blockKopf}>
        <div style={S.ampelPunkt(a.farbe)}>{a.zeichen}</div>
        <h3 style={S.blockTitel}>{q.name}</h3>
        <div style={S.blockLauf}>{q.lauf_text}</div>
      </div>

      <div style={S.inhalt}>
        {sauber && <div style={S.okZeile}>✓ {q.ok_text}</div>}
        {q.key === "bilder_kompr" && <BilderKomprConfig />}
        {q.key === "renameme" && <RenamemeConfig autoInfo={q.auto_info} />}

        {handlung.length > 0 && (
          <div style={S.abschnitt}>
            <div style={{ ...S.abschnittTitel, color: "var(--color-error)" }}>
              {handlung.length === 1 ? "Das braucht deine Hilfe" : `${handlung.length} Dinge brauchen deine Hilfe`}
            </div>
            {handlung.map((h, i) => (
              <div key={i} style={S.punkt}>
                <div style={S.punktTitel}>{h.titel}</div>
                <div style={{ ...S.punktProblem, color: "var(--color-error)" }}>{h.problem}</div>
                {h.was_tun && <div style={S.punktTun}><strong>Zu tun:</strong> {h.was_tun}</div>}
                {h.dateien?.length > 0 && (
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--color-gray-600)" }}>
                      Betroffene Dateien anzeigen ({h.dateien.length})
                    </summary>
                    <div style={{ marginTop: 6 }}>
                      {h.dateien.map((d, k) => (
                        <div key={k} style={{ fontSize: "0.78rem", padding: "2px 0", wordBreak: "break-all", color: "var(--color-gray-700)" }}>
                          · {d}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {(h.zusatz || h.wann) && (
                  <div style={S.punktZusatz}>
                    {h.zusatz}{h.zusatz && h.wann ? " · " : ""}{h.wann ? zeit(h.wann) : ""}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {h.aktion === "datev_erneut" && (
                    <button
                      style={S.btnKlein}
                      disabled={laeuftAktion === (h.schluessel || h.titel)}
                      onClick={() => aktion("datev_erneut", h)}
                    >
                      {laeuftAktion === (h.schluessel || h.titel) ? "Sendet …" : "Erneut senden"}
                    </button>
                  )}
                  <button
                    style={S.btnKlein}
                    disabled={laeuftAktion === (h.schluessel || h.titel)}
                    onClick={() => aktion("quittieren", h)}
                    title="Diesen Punkt dauerhaft ausblenden"
                  >
                    Kein Problem
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {hinweise.length > 0 && (
          <div style={S.abschnitt}>
            <div style={{ ...S.abschnittTitel, color: "var(--color-warning)" }}>
              Zur Kenntnis ({hinweise.length})
            </div>
            {hinweise.map((h, i) => (
              <div key={i} style={S.punktHinweis}>
                <div style={S.punktTitel}>{h.titel}</div>
                <div style={S.punktProblem}>{h.text}</div>
                {h.abhakbar && (
                  <button
                    style={{ ...S.btnKlein, marginTop: 8 }}
                    disabled={laeuftAktion === (h.schluessel || h.titel)}
                    onClick={() => aktion("quittieren", h)}
                    title="Dauerhaft ausblenden — z.B. weil es das Konto nicht mehr gibt"
                  >
                    Kein Problem — nicht mehr anzeigen
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {(q.konten || []).length > 0 && (
          <div style={S.abschnitt}>
            <button style={S.aufklapp} onClick={() => setZeigeKonten(!zeigeKonten)}>
              {zeigeKonten ? "▲ " : "▼ "}Datenstand je Konto ({q.konten.length})
              {(() => {
                const alt = q.konten.filter((k) => k.stufe !== "aktuell" && k.stufe !== "geschlossen").length;
                return alt ? ` — ${alt} nicht aktuell` : " — alle aktuell";
              })()}
            </button>
            {zeigeKonten && (
              <div style={{ marginTop: 8 }}>
                {q.konten.map((k, i) => {
                  const zu = k.stufe === "geschlossen";
                  const farbe = zu ? "var(--color-gray-500)"
                    : k.stufe === "aktuell" ? "var(--color-success)"
                    : k.stufe === "aelter" ? "var(--color-warning)" : "var(--color-error)";
                  const label = zu ? (k.geschlossen === "inaktiv" ? "inaktiv" : "geschlossen")
                    : k.stufe === "aktuell" ? "aktuell"
                    : k.stufe === "aelter" ? "etwas älter" : "überfällig";
                  const busy = laeuftAktion === k.konto;
                  return (
                    <div key={i} style={{ ...S.erledigtZeile, alignItems: "flex-start", opacity: zu ? 0.7 : 1 }}>
                      <span style={{ color: farbe, marginTop: 2 }}>●</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ wordBreak: "break-all" }}>
                          {k.konto}{k.bank ? <span style={{ color: "var(--color-gray-500)" }}> · {k.bank}</span> : null}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "var(--color-gray-500)" }}>
                          {k.letzter_umsatz ? `letzter Umsatz: ${k.letzter_umsatz}` : ""}
                        </div>
                      </div>
                      <span style={{ color: "var(--color-gray-600)", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                        {zu ? "" : `CSV geladen: ${zeit(k.zuletzt_geladen)} · ${k.tage} T`}
                      </span>
                      <span style={{ color: farbe, whiteSpace: "nowrap", fontWeight: 600, minWidth: 70, textAlign: "right" }}>
                        {label}
                      </span>
                      <button
                        style={{ ...S.btnKlein, padding: "2px 7px", fontSize: "0.72rem" }}
                        disabled={busy}
                        title={zu ? "Konto wieder als aktiv führen" : "Konto als geschlossen markieren — mahnt dann nicht mehr"}
                        onClick={() => aktion("konto_status", { iban: k.konto, status: zu ? null : "geschlossen", schluessel: k.konto })}
                      >
                        {busy ? "…" : zu ? "aktivieren" : "geschlossen"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {erledigt.length > 0 && (
          <div style={S.abschnitt}>
            <button style={S.aufklapp} onClick={() => setZeigeErledigt(!zeigeErledigt)}>
              {zeigeErledigt ? "▲ " : "▼ "}{erledigt.length} Datei(en) beim letzten Lauf verarbeitet
            </button>
            {zeigeErledigt && (
              <div style={{ marginTop: 8 }}>
                {erledigt.map((e, i) => (
                  <div key={i} style={S.erledigtZeile}>
                    <span style={{ color: "var(--color-success)" }}>✓</span>
                    <span style={{ flex: 1, wordBreak: "break-all" }}>{e.csv_filename}</span>
                    <span style={{ color: "var(--color-gray-600)", whiteSpace: "nowrap" }}>
                      {e.rows_inserted != null ? `${e.rows_inserted} Zeilen` : ""}
                      {e.new_excel_filename ? " → Excel" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PostkorbPage() {
  const [daten, setDaten] = useState(() => cacheLesen(CACHE_KEY, 24 * 3600));
  const [laeufe, setLaeufe] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState("");
  const [geminiStatus, setGeminiStatus] = useState(null);
  const [geminiBusy, setGeminiBusy] = useState(false);

  // frisch=true übergeht den Zwischenspeicher des Servers (dauert dann ~15s,
  // weil drei Dropbox-Ordner gelesen werden)
  const laden = useCallback(async (frisch = false) => {
    setLaedt(true); setFehler("");
    try {
      const [u, l, g] = await Promise.all([
        fetch(`${API}/uebersicht${frisch ? "?frisch=1" : ""}`).then((r) => r.json()),
        fetch(`${API}/laeufe?limit=25`).then((r) => r.json()),
        fetch(`${API}/gemini-status`).then((r) => r.json()).catch(() => null),
      ]);
      if (u?.success) {
        setDaten(u);
        cacheSchreiben(CACHE_KEY, u);
      } else setFehler("Übersicht konnte nicht geladen werden");
      if (l?.success) setLaeufe(l.laeufe || []);
      if (g) setGeminiStatus(g);
    } catch (e) {
      setFehler(String(e?.message || e));
    } finally { setLaedt(false); }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // Gemini-Statuscheck erzwingen (echter Ping)
  const geminiTesten = useCallback(async () => {
    setGeminiBusy(true);
    try {
      const g = await fetch(`${API}/gemini-status?frisch=1`).then((r) => r.json());
      setGeminiStatus(g);
      geminiSperreNeuLaden();   // Balken oben und großer Block gleich mitziehen
    } catch (e) {
      setGeminiStatus({ ok: false, key_gesetzt: false, fehler: String(e?.message || e) });
    } finally { setGeminiBusy(false); }
  }, []);

  // „Erneut senden“ und „Kein Problem“ — danach wird neu geladen, damit die
  // Liste sofort zeigt, was der Klick bewirkt hat.
  const aktionAusfuehren = useCallback(async (typ, quelle, punkt) => {
    try {
      if (typ === "datev_erneut") {
        await fetch(`${API}/datev/erneut-senden`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ dateien: [punkt.schluessel || punkt.titel] }),
        });
      } else if (typ === "quittieren") {
        await fetch(`${API}/quittieren`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ quelle, schluessel: punkt.schluessel || punkt.titel }),
        });
      } else if (typ === "konto_status") {
        await fetch(`${API}/konto-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ iban: punkt.iban, status: punkt.status || null }),
        });
      }
    } catch (e) {
      setFehler(String(e?.message || e));
    }
    await laden();
  }, [laden]);

  const gesamt = daten?.gesamt_handlung || 0;
  const offen = daten?.gesamt_offen || 0;
  const kopfFarbe = gesamt ? "var(--color-error)" : (offen ? "var(--color-warning)" : "var(--color-success)");

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Postkorb</h1>
      <p style={S.sub}>Was gerade deine Aufmerksamkeit braucht.</p>
      <GeminiWarnung gross />

      {(() => {
        const gs = geminiStatus;
        const ok = gs && gs.ok && gs.antwort_ok;
        const warn = gs && gs.ok && !gs.antwort_ok;
        const farbe = !gs ? "var(--color-gray-400)"
          : ok ? "var(--color-success)" : warn ? "var(--color-warning)" : "var(--color-error)";
        const txt = !gs ? "Gemini: Status wird geprüft …"
          : !gs.key_gesetzt ? "Gemini: kein API-Key"
          : ok ? `Gemini aktiv & erreichbar${gs.ms ? ` · ${gs.ms} ms` : ""}`
          : warn ? "Gemini erreichbar, aber Antwort unerwartet"
          : `Gemini-Fehler${gs.fehler ? `: ${String(gs.fehler).slice(0, 70)}` : ""}`;
        const zeichen = !gs ? "…" : ok ? "✓" : warn ? "!" : "×";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 14px", fontSize: "0.82rem" }}>
            <span style={{ ...S.ampelPunkt(farbe), width: 18, height: 18, fontSize: "0.7rem" }}>{zeichen}</span>
            <span style={{ color: "var(--color-gray-600)" }}>{txt}</span>
            {gs && gs.geprueft_am && (
              <span style={{ color: "var(--color-gray-400)", fontSize: "0.72rem" }}>
                ({gs.aus_zwischenspeicher ? "gecacht" : "frisch"} · {String(gs.geprueft_am).slice(11, 16)})
              </span>
            )}
            <button style={{ ...S.btn, padding: "2px 10px", fontSize: "0.75rem" }} onClick={geminiTesten} disabled={geminiBusy}>
              {geminiBusy ? "testet …" : "Jetzt testen"}
            </button>
          </div>
        );
      })()}

      {fehler && (
        <div style={{ ...S.block, borderColor: "var(--color-error)", color: "var(--color-error)", padding: 16 }}>{fehler}</div>
      )}

      {laedt && !daten ? (
        <p style={S.leer}>Wird geladen …</p>
      ) : (
        <>
          <div style={{ ...S.kopfBox, background: "var(--bg-card)", border: `1px solid ${kopfFarbe}`, borderLeft: `4px solid ${kopfFarbe}` }}>
            <span style={{ ...S.kopfZahl, color: kopfFarbe }}>{gesamt || (offen ? offen : "✓")}</span>
            <span style={{ ...S.kopfText, color: kopfFarbe }}>
              {gesamt
                ? (gesamt === 1 ? "Sache braucht deine Hilfe" : "Sachen brauchen deine Hilfe")
                : (offen ? (offen === 1 ? "Hinweis zur Kenntnis" : "Hinweise zur Kenntnis") : "Alles erledigt — nichts zu tun")}
            </span>
            <button style={{ ...S.btn, marginLeft: "auto" }} onClick={() => laden(true)} disabled={laedt}>
              {laedt ? "Lädt …" : "Neu prüfen"}
            </button>
          </div>

          {(daten?.quellen || []).map((q) => (
            <Block key={q.key} q={q} onAktion={aktionAusfuehren} />
          ))}

          <div style={S.historie}>
            <h3 style={S.histTitel}>Wann was gelaufen ist</h3>
            {laeufe.length === 0 ? (
              <p style={S.leer}>Noch keine Durchläufe aufgezeichnet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Automatik</th>
                      <th style={S.th}>Gestartet</th>
                      <th style={S.th}>Ergebnis</th>
                      <th style={S.th}>Ausgelöst</th>
                      <th style={S.th}>Meldung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laeufe.map((l) => (
                      <tr key={l.id}>
                        <td style={S.td}>{l.quelle}</td>
                        <td style={S.td}>{zeit(l.gestartet_am)}</td>
                        <td style={S.td}>
                          {l.anzahl_ok} erledigt
                          {l.anzahl_offen ? ` · ${l.anzahl_offen} liegen geblieben` : ""}
                          {l.status === "fehler" ? " · abgebrochen" : ""}
                        </td>
                        <td style={S.td}>{l.ausgeloest_von === "manuell" ? "von Hand" : "nach Plan"}</td>
                        <td style={S.td}>{l.meldung || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
